import * as joint from "@clientio/rappid";
import { dia } from "@clientio/rappid";
import { State, StateView } from "../state";
import { PreferenceStore } from "../../services/preference-store";
import { getSubdiagramCells } from "../../subdiagram-viewer";
import { BaseNode, BaseView } from "../base/base-node";
import { Transition, TransitionView } from "../transition";
import { RappidCoreContext } from "../../rappid/rappid-core-context";
import { Highlighter } from "../../services/highlighter";

class PreviewContext {
  private static INSTANCE: RappidCoreContext;

  static getInstance() {
    if (!PreviewContext.INSTANCE) {
      PreviewContext.INSTANCE = new RappidCoreContext();
    }
    return PreviewContext.INSTANCE;
  }
}

class PreviewGraph {
  private static INSTANCE: joint.dia.Graph;
  private static EMPTY_RECT_INSTANCE: joint.g.Rect;
  static getInstance() {
    if (!PreviewGraph.INSTANCE) {
      PreviewGraph.INSTANCE = new joint.dia.Graph();
    }
    return PreviewGraph.INSTANCE;
  }

  static emptyRect() {
    if (!PreviewGraph.EMPTY_RECT_INSTANCE) {
      PreviewGraph.EMPTY_RECT_INSTANCE = new joint.g.Rect(0, 0, 100, 100);
    }
    return PreviewGraph.EMPTY_RECT_INSTANCE;
  }
}

export class SubdiagramHighlighter extends dia.HighlighterView {
  private popup: joint.ui.Popup;
  private popupTimer: NodeJS.Timeout;
  private indicatorElement: SVGElement;
  private themeChangeHandler: () => void;
  private mouseEnterHandler: () => void;
  private mouseLeaveHandler: () => void;
  private clickHandler: (evt: MouseEvent) => void;
  private updatePositionHandler: () => void;

  highlight(cellView: dia.CellView): void {
    const view = cellView as StateView;

    this.indicatorElement = this.createIndicator(view);
    view.el.appendChild(this.indicatorElement);

    // Setup event handlers
    this.mouseEnterHandler = () => this.addPopup(view);
    this.mouseLeaveHandler = () => this.removePopup();
    this.clickHandler = (evt: MouseEvent) => this.handleClick(evt, view);
    this.themeChangeHandler = () => this.updateIndicator(view);
    this.updatePositionHandler = () => {
      this.updateIndicatorPosition(view);
      this.removePopup();
    };

    this.indicatorElement.addEventListener("mouseenter", this.mouseEnterHandler);
    this.indicatorElement.addEventListener("mouseleave", this.mouseLeaveHandler);
    this.indicatorElement.addEventListener("click", this.clickHandler);
    view.model.on("change:position change:size", this.updatePositionHandler, this);
    PreferenceStore.INSTANCE.on("change:theme", this.themeChangeHandler);
  }

  unhighlight(cellView: dia.CellView): void {
    const view = cellView as StateView;

    this.removePopup();

    if (this.indicatorElement) {
      this.indicatorElement.removeEventListener("mouseenter", this.mouseEnterHandler);
      this.indicatorElement.removeEventListener("mouseleave", this.mouseLeaveHandler);
      this.indicatorElement.removeEventListener("click", this.clickHandler);
      this.indicatorElement.remove();
      this.indicatorElement = null;
    }

    view.model.off("change:position change:size", this.updatePositionHandler, this);
    PreferenceStore.INSTANCE.off("change:theme", this.themeChangeHandler);
  }

  private createIndicator(view: StateView): SVGElement {
    const bbox = view.model.getBBox();
    const themeClass = PreferenceStore.INSTANCE.isDark ? "joint-theme-dark" : "joint-theme-default";

    const g = document.createElementNS("http://www.w3.org/2000/svg", "g");
    g.setAttribute("class", "subdiagram-indicator");
    g.setAttribute("transform", `translate(${bbox.width - 16}, ${bbox.height - 16})`);

    const background = document.createElementNS("http://www.w3.org/2000/svg", "rect");
    background.setAttribute("x", "0");
    background.setAttribute("y", "0");
    background.setAttribute("width", "14");
    background.setAttribute("height", "14");
    background.setAttribute("rx", "2");
    background.setAttribute("cursor", "pointer");
    background.setAttribute("class", `joint-type-region ${themeClass}`);
    background.setAttribute("fill-opacity", "1");
    background.setAttribute("stroke-width", "0");
    g.appendChild(background);

    const foreignObject = document.createElementNS("http://www.w3.org/2000/svg", "foreignObject");
    foreignObject.setAttribute("x", "0");
    foreignObject.setAttribute("y", "0");
    foreignObject.setAttribute("width", "14");
    foreignObject.setAttribute("height", "14");
    foreignObject.setAttribute("pointer-events", "none");

    const iconDiv = document.createElement("div");
    iconDiv.style.display = "flex";
    iconDiv.style.alignItems = "center";
    iconDiv.style.justifyContent = "center";
    iconDiv.style.width = "100%";
    iconDiv.style.height = "100%";
    iconDiv.innerHTML = '<i class="mdi mdi-layers-outline" style="font-size: 8px;"></i>';

    foreignObject.appendChild(iconDiv);
    g.appendChild(foreignObject);

    return g;
  }

  private updateIndicator(view: StateView): void {
    if (this.indicatorElement) {
      this.indicatorElement.remove();
      this.indicatorElement = this.createIndicator(view);
      view.el.appendChild(this.indicatorElement);

      this.indicatorElement.addEventListener("mouseenter", this.mouseEnterHandler);
      this.indicatorElement.addEventListener("mouseleave", this.mouseLeaveHandler);
      this.indicatorElement.addEventListener("click", this.clickHandler);
    }
  }

  private updateIndicatorPosition(view: StateView): void {
    if (this.indicatorElement) {
      const bbox = view.model.getBBox();
      this.indicatorElement.setAttribute("transform", `translate(${bbox.width - 16}, ${bbox.height - 16})`);
    }
  }

  private handleClick(evt: MouseEvent, view: StateView): void {
    evt.stopPropagation();
    evt.preventDefault();
    view.trigger("subdiagram:into");
  }

  private addPopup(view: StateView): void {
    if (this.popup) return;

    this.popupTimer = setTimeout(() => {
      this.createSubdiagramPopup(view);
    }, 200);
  }

  private createSubdiagramPopup(view: StateView): void {
    const model = view.model as State;
    this.popup = new joint.ui.Popup({
      content: function (el) {
        const graph = PreviewGraph.getInstance();
        graph.resetCells([]);
        const cells = getSubdiagramCells(model);
        graph.addCells(cells);
        const previewContext = PreviewContext.getInstance();
        previewContext.graph.fromJSON(graph.toJSON(), { mergeArrays: true });
        addDiagnostics(previewContext, cells);
        displayCells(previewContext);
        adjustPaper(previewContext);
        addHighlighter(previewContext);
        el.appendChild(previewContext.paper.el);
      },
      target: view.el,
    }).render();
  }

  private removePopup = (): void => {
    clearTimeout(this.popupTimer);

    if (this.popup) {
      this.popup.remove();
      this.popup = null;
      removeHighlighter(PreviewContext.getInstance());
    }
  };
}

function addHighlighter(previewContext: RappidCoreContext) {
  const highlighter = Highlighter.getInstance();
  highlighter.addContext(previewContext);
  highlighter.showAll();
}

function removeHighlighter(previewContext: RappidCoreContext) {
  const highlighter = Highlighter.getInstance();
  highlighter.removeContext(previewContext);
}

function adjustPaper(previewContext: RappidCoreContext) {
  const bbox = previewContext.graph.getBBox() ?? PreviewGraph.emptyRect();
  const padding = 20;
  previewContext.paper.el.style.width = `${bbox.width + 2 * padding}px`;
  previewContext.paper.el.style.height = `${bbox.height + 2 * padding}px`;

  previewContext.paper.once("render:done", () => {
    previewContext.paper.setDimensions(bbox.width + 2 * padding, bbox.height + 2 * padding);
    previewContext.paper.translate(padding - bbox.x, padding - bbox.y);
  });
}

function displayCells(previewContext: RappidCoreContext) {
  previewContext.graph.getCells().forEach((c) => {
    c.attr("root/display", "");
    const view = c.findView(previewContext.paper);
    if (view instanceof BaseView || view instanceof TransitionView) {
      view.displayDiagnostics();
    }
  });
}

function addDiagnostics(
  previewContext: RappidCoreContext,
  cells: joint.dia.Cell<joint.dia.Cell.Attributes, joint.dia.ModelSetOptions>[]
) {
  previewContext.graph.getCells().forEach((c) => {
    const found = cells.find((oldCell) => oldCell.id === c.id);
    if (
      found &&
      (found instanceof BaseNode || found instanceof Transition) &&
      (c instanceof BaseNode || c instanceof Transition)
    ) {
      c.errors = found.errors;
      c.warnings = found.warnings;
    }
  });
}
